import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { useHelpdeskLanguage } from '../../context/HelpdeskLanguageContext';
import './Helpdesk.css';

function HelpdeskCreateTicket() {
    const { t, language, toggleLanguage, isRTL } = useHelpdeskLanguage();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [agents, setAgents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]); // NEW
    const [error, setError] = useState('');
    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const clientDropdownRef = useRef(null);
    
    // New client confirmation modal
    const [showNewClientModal, setShowNewClientModal] = useState(false);
    const [pendingEmail, setPendingEmail] = useState('');
    
    // Get saved department from localStorage
    const getSavedDepartment = () => {
        return localStorage.getItem('helpdesk_last_department') || '';
    };
    
    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        priority: 'medium',
        category_id: '',
        department_id: getSavedDepartment(),
        sub_department_id: '', // NEW
        client_id: '',
        client_name: '',
        client_email: '',
        client_phone: '',
        client_company: '',
        assigned_to: ''
    });
    
    const [attachments, setAttachments] = useState([]);
    const [isNewClient, setIsNewClient] = useState(false);

    useEffect(() => {
        loadData();
        
        const handleClickOutside = (e) => {
            if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
                setShowClientDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Server-side client search with debounce
    useEffect(() => {
        if (!clientSearch || clientSearch.length < 2) {
            setFilteredClients([]);
            return;
        }
        
        const timer = setTimeout(async () => {
            try {
                const res = await api.get('/helpdesk-agent/clients', {
                    params: { search: clientSearch, limit: 50 }
                });
                if (res.data.success) {
                    const sorted = (res.data.clients || []).sort((a, b) => 
                        (a.name || '').localeCompare(b.name || '')
                    );
                    setFilteredClients(sorted);
                }
            } catch (err) {
                console.error('Client search failed:', err);
            }
        }, 300);
        
        return () => clearTimeout(timer);
    }, [clientSearch]);

    const loadData = async () => {
        try {
            const categoriesRes = await api.get('/helpdesk-agent/categories');
            if (categoriesRes.data.success) {
                setCategories(categoriesRes.data.categories || []);
            }

            // Load departments (only allowed ones for this agent)
            const departmentsRes = await api.get('/helpdesk-agent/my-departments');
            if (departmentsRes.data.success) {
                const depts = departmentsRes.data.departments || [];
                setDepartments(depts);
                
                // Verify saved department is still valid
                const savedDeptId = getSavedDepartment();
                if (savedDeptId) {
                    const isValidDept = depts.some(d => d.id.toString() === savedDeptId);
                    if (!isValidDept && !departmentsRes.data.canViewAll) {
                        localStorage.removeItem('helpdesk_last_department');
                        setFormData(prev => ({ ...prev, department_id: depts.length > 0 ? depts[0].id.toString() : '' }));
                    }
                } else if (depts.length === 1) {
                    setFormData(prev => ({ ...prev, department_id: depts[0].id.toString() }));
                }
            }
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };

    // Load agents AND sub-departments when department changes
    useEffect(() => {
        const loadDepartmentData = async () => {
            if (formData.department_id) {
                try {
                    // Load agents for department
                    const agentsRes = await api.get(`/helpdesk-agent/agents?department_id=${formData.department_id}`);
                    if (agentsRes.data.success) {
                        setAgents(agentsRes.data.agents || []);
                        if (formData.assigned_to) {
                            const agentStillValid = (agentsRes.data.agents || []).some(a => a.id.toString() === formData.assigned_to);
                            if (!agentStillValid) {
                                setFormData(prev => ({ ...prev, assigned_to: '' }));
                            }
                        }
                    }

                    // Load sub-departments for department
                    const subDeptRes = await api.get(`/helpdesk-agent/departments/${formData.department_id}/sub-departments`);
                    if (subDeptRes.data.success) {
                        setSubDepartments(subDeptRes.data.sub_departments || []);
                    } else {
                        setSubDepartments([]);
                    }
                } catch (error) {
                    console.error('Failed to load department data:', error);
                    setAgents([]);
                    setSubDepartments([]);
                }
            } else {
                setAgents([]);
                setSubDepartments([]);
            }
            // Clear sub_department_id when department changes
            setFormData(prev => ({ ...prev, sub_department_id: '' }));
        };
        
        loadDepartmentData();
    }, [formData.department_id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Save department selection to localStorage
        if (name === 'department_id' && value) {
            localStorage.setItem('helpdesk_last_department', value);
        }
    };

    const handleClientSelect = (client) => {
        setFormData(prev => ({
            ...prev,
            client_id: client.id,
            client_name: client.name,
            client_email: client.email,
            client_phone: client.phone || '',
            client_company: client.company || ''
        }));
        setClientSearch(client.name + ' (' + client.email + ')');
        setShowClientDropdown(false);
        setIsNewClient(false);
    };

    const handleClientSearchChange = (e) => {
        setClientSearch(e.target.value);
        setShowClientDropdown(true);
        if (!e.target.value) {
            setFormData(prev => ({
                ...prev,
                client_id: '',
                client_name: '',
                client_email: '',
                client_phone: '',
                client_company: ''
            }));
        }
    };

    const handleNewClientToggle = () => {
        setIsNewClient(!isNewClient);
        if (!isNewClient) {
            setFormData(prev => ({
                ...prev,
                client_id: '',
                client_name: '',
                client_email: '',
                client_phone: '',
                client_company: ''
            }));
            setClientSearch('');
        }
    };

    // Check if email exists when user leaves the email field
    const handleEmailBlur = () => {
        if (isNewClient || !formData.client_email || formData.client_id) return;
        
        const email = formData.client_email.trim().toLowerCase();
        if (!email || !email.includes('@')) return;
        
        const existingClient = clients.find(c => c.email?.toLowerCase() === email);
        
        if (existingClient) {
            handleClientSelect(existingClient);
        } else {
            setPendingEmail(email);
            setShowNewClientModal(true);
        }
    };

    const handleConfirmNewClient = () => {
        setIsNewClient(true);
        setFormData(prev => ({
            ...prev,
            client_email: pendingEmail,
            client_name: '',
            client_phone: '',
            client_company: ''
        }));
        setShowNewClientModal(false);
        setPendingEmail('');
    };

    const handleCancelNewClient = () => {
        setFormData(prev => ({
            ...prev,
            client_email: ''
        }));
        setShowNewClientModal(false);
        setPendingEmail('');
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const maxSize = 10 * 1024 * 1024;
        
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                setError(t('fileTooLarge'));
                return false;
            }
            return true;
        });

        if (attachments.length + validFiles.length > 5) {
            setError(t('maxFilesError'));
            return;
        }

        setAttachments(prev => [...prev, ...validFiles]);
        setError('');
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.subject.trim()) {
            setError(t('subjectRequired'));
            return;
        }

        if (!formData.description.trim()) {
            setError(t('descriptionRequired'));
            return;
        }

        if (!formData.department_id) {
            setError(language === 'ar' ? 'القسم مطلوب' : 'Department is required');
            return;
        }

        if (!formData.client_id && !formData.client_email) {
            setError(t('clientRequired'));
            return;
        }

        if (!formData.client_id && formData.client_email && !isNewClient) {
            const email = formData.client_email.trim().toLowerCase();
            const existingClient = clients.find(c => c.email?.toLowerCase() === email);
            
            if (!existingClient) {
                setPendingEmail(email);
                setShowNewClientModal(true);
                return;
            }
        }

        try {
            setLoading(true);

            const submitData = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key]) {
                    submitData.append(key, formData[key]);
                }
            });

            attachments.forEach(file => {
                submitData.append('attachments', file);
            });

            const response = await api.post('/helpdesk-agent/tickets', submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                navigate('/helpdesk/tickets/' + response.data.ticket.id, {
                    state: { success: true, message: t('ticketCreatedSuccess') }
                });
            } else {
                setError(response.data.message || t('createTicketFailed'));
            }
        } catch (error) {
            console.error('Create ticket error:', error);
            setError(error.response?.data?.message || t('createTicketFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className={'hd-create-ticket ' + (isRTL ? 'rtl' : 'ltr')} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="hd-header">
                    <div className="hd-header-left">
                        <Link to="/helpdesk" className="hd-back-link">
                            {isRTL ? '→' : '←'} {t('backToTickets')}
                        </Link>
                        <h1>🎫 {t('createNewTicket')}</h1>
                    </div>
                    <button onClick={toggleLanguage} className="hd-btn hd-btn-lang">
                        🌐 {language === 'ar' ? 'English' : 'العربية'}
                    </button>
                </div>

                {error && <div className="hd-alert hd-alert-error">{error}</div>}

                {/* New Client Confirmation Modal */}
                {showNewClientModal && (
                    <div className="hd-modal-overlay">
                        <div className="hd-modal">
                            <div className="hd-modal-header">
                                <h3>⚠️ {t('newClientConfirmation')}</h3>
                            </div>
                            <div className="hd-modal-body">
                                <p className="hd-modal-message">
                                    {t('emailNotRegistered')}
                                </p>
                                <div className="hd-modal-email-display">
                                    <span className="hd-modal-label">{t('email')}:</span>
                                    <span className="hd-modal-value">{pendingEmail}</span>
                                </div>
                                <p className="hd-modal-note">
                                    {t('newClientWillBeCreated')}
                                </p>
                            </div>
                            <div className="hd-modal-actions">
                                <button 
                                    onClick={handleCancelNewClient} 
                                    className="hd-btn hd-btn-secondary"
                                >
                                    {t('goBack')}
                                </button>
                                <button 
                                    onClick={handleConfirmNewClient} 
                                    className="hd-btn hd-btn-primary"
                                >
                                    {t('continue')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="hd-form">
                    <div className="hd-form-grid">
                        <div className="hd-form-section">
                            <h3>{t('ticketDetails')}</h3>
                            
                            <div className="hd-form-group">
                                <label>{t('subject')} *</label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    placeholder={t('enterSubject')}
                                    className="hd-input"
                                />
                            </div>

                            <div className="hd-form-group">
                                <label>{t('description')} *</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder={t('describeIssue')}
                                    rows={6}
                                    className="hd-textarea"
                                />
                            </div>

                            <div className="hd-form-row">
                                <div className="hd-form-group">
                                    <label>{t('priority')}</label>
                                    <select
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleChange}
                                        className="hd-select"
                                    >
                                        <option value="low">{t('priorityLow')}</option>
                                        <option value="medium">{t('priorityMedium')}</option>
                                        <option value="high">{t('priorityHigh')}</option>
                                        <option value="critical">{t('priorityCritical')}</option>
                                    </select>
                                </div>

                                <div className="hd-form-group">
                                    <label>{t('category')}</label>
                                    <select
                                        name="category_id"
                                        value={formData.category_id}
                                        onChange={handleChange}
                                        className="hd-select"
                                    >
                                        <option value="">{t('selectCategory')}</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Department - Required */}
                            <div className="hd-form-group">
                                <label>{language === 'ar' ? 'القسم' : 'Department'} *</label>
                                <select
                                    name="department_id"
                                    value={formData.department_id}
                                    onChange={handleChange}
                                    className="hd-select"
                                    required
                                    style={{ borderColor: !formData.department_id ? '#e74c3c' : undefined }}
                                >
                                    <option value="">{language === 'ar' ? '-- اختر القسم --' : '-- Select Department --'}</option>
                                    {departments.map(dept => (
                                        <option key={dept.id} value={dept.id}>
                                            {language === 'ar' && dept.name_ar ? dept.name_ar : dept.name}
                                        </option>
                                    ))}
                                </select>
                                {departments.length === 0 && (
                                    <span className="hd-hint" style={{ color: '#e74c3c' }}>
                                        {language === 'ar' ? 'لا توجد أقسام متاحة. تواصل مع المسؤول.' : 'No departments available. Contact admin.'}
                                    </span>
                                )}
                            </div>

                            {/* Sub-Department - Optional, shown only when department has sub-departments */}
                            {formData.department_id && subDepartments.length > 0 && (
                                <div className="hd-form-group">
                                    <label>{language === 'ar' ? 'التقسيم الفرعي' : 'Sub-Department'}</label>
                                    <select
                                        name="sub_department_id"
                                        value={formData.sub_department_id}
                                        onChange={handleChange}
                                        className="hd-select"
                                    >
                                        <option value="">{language === 'ar' ? '-- اختر التقسيم الفرعي --' : '-- Select Sub-Department --'}</option>
                                        {subDepartments.map(sd => (
                                            <option key={sd.id} value={sd.id}>
                                                {language === 'ar' && sd.name_ar ? sd.name_ar : sd.name}
                                            </option>
                                        ))}
                                    </select>
                                    <small style={{ color: '#888', marginTop: '4px', display: 'block' }}>
                                        {language === 'ar' 
                                            ? 'اختيار التقسيم الفرعي يفعّل التعيين التلقائي بالتبادل' 
                                            : 'Selecting a sub-department enables auto-assignment via round-robin'}
                                    </small>
                                </div>
                            )}

                            <div className="hd-form-group">
                                <label>{t('assignTo')}</label>
                                <select
                                    name="assigned_to"
                                    value={formData.assigned_to}
                                    onChange={handleChange}
                                    className="hd-select"
                                    disabled={!formData.department_id}
                                >
                                    <option value="">{t('unassigned')}</option>
                                    {agents.map(agent => (
                                        <option key={agent.id} value={agent.id}>{agent.full_name}</option>
                                    ))}
                                </select>
                                {!formData.department_id && (
                                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                                        {language === 'ar' ? 'اختر القسم أولاً' : 'Select department first'}
                                    </small>
                                )}
                                {formData.department_id && agents.length === 0 && (
                                    <small style={{ color: '#e67e22', marginTop: '4px', display: 'block' }}>
                                        {language === 'ar' ? 'لا يوجد وكلاء في هذا القسم' : 'No agents in this department'}
                                    </small>
                                )}
                            </div>

                            <div className="hd-form-group">
                                <label>{t('attachments')}</label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    multiple
                                    style={{ display: 'none' }}
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="hd-btn hd-btn-secondary"
                                >
                                    📎 {t('addFiles')}
                                </button>
                                <span className="hd-hint">{t('maxFilesHint')}</span>

                                {attachments.length > 0 && (
                                    <div className="hd-attachment-list">
                                        {attachments.map((file, index) => (
                                            <div key={index} className="hd-attachment-item">
                                                <span>📄 {file.name} ({formatFileSize(file.size)})</span>
                                                <button type="button" onClick={() => removeAttachment(index)}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="hd-form-section">
                            <h3>{t('clientInformation')}</h3>
                            
                            <div className="hd-form-group">
                                <label className="hd-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isNewClient}
                                        onChange={handleNewClientToggle}
                                    />
                                    {t('createNewClient')}
                                </label>
                            </div>

                            {!isNewClient && (
                                <div className="hd-form-group" ref={clientDropdownRef}>
                                    <label>{t('searchClient')} *</label>
                                    <input
                                        type="text"
                                        value={clientSearch}
                                        onChange={handleClientSearchChange}
                                        onFocus={() => setShowClientDropdown(true)}
                                        placeholder={t('searchClientPlaceholder')}
                                        className="hd-input"
                                        autoComplete="off"
                                    />
                                    {showClientDropdown && filteredClients.length > 0 && (
                                        <div className="hd-client-dropdown">
                                            {filteredClients.map(client => (
                                                <div 
                                                    key={client.id} 
                                                    className="hd-client-option"
                                                    onClick={() => handleClientSelect(client)}
                                                >
                                                    <div className="hd-client-name">{client.name}</div>
                                                    <div className="hd-client-details">
                                                        {client.email}
                                                        {client.company && ' - ' + client.company}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {showClientDropdown && clientSearch && filteredClients.length === 0 && (
                                        <div className="hd-client-dropdown">
                                            <div className="hd-client-option hd-no-results">
                                                {t('noClientsFound')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="hd-form-group">
                                <label>{t('clientName')} {isNewClient && '*'}</label>
                                <input
                                    type="text"
                                    name="client_name"
                                    value={formData.client_name}
                                    onChange={handleChange}
                                    placeholder={t('clientName')}
                                    className="hd-input"
                                    readOnly={!isNewClient && formData.client_id}
                                />
                            </div>

                            <div className="hd-form-group">
                                <label>{t('clientEmail')} {isNewClient && '*'}</label>
                                <input
                                    type="email"
                                    name="client_email"
                                    value={formData.client_email}
                                    onChange={handleChange}
                                    onBlur={handleEmailBlur}
                                    placeholder="client@example.com"
                                    className="hd-input"
                                    readOnly={!isNewClient && formData.client_id}
                                />
                            </div>

                            <div className="hd-form-group">
                                <label>{t('clientPhone')}</label>
                                <input
                                    type="tel"
                                    name="client_phone"
                                    value={formData.client_phone}
                                    onChange={handleChange}
                                    placeholder={t('phoneNumber')}
                                    className="hd-input"
                                    readOnly={!isNewClient && formData.client_id}
                                />
                            </div>

                            <div className="hd-form-group">
                                <label>{t('company')}</label>
                                <input
                                    type="text"
                                    name="client_company"
                                    value={formData.client_company}
                                    onChange={handleChange}
                                    placeholder={t('companyName')}
                                    className="hd-input"
                                    readOnly={!isNewClient && formData.client_id}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="hd-form-actions">
                        <Link to="/helpdesk" className="hd-btn hd-btn-secondary">
                            {t('cancel')}
                        </Link>
                        <button type="submit" className="hd-btn hd-btn-primary" disabled={loading}>
                            {loading ? t('creating') : '🎫 ' + t('createTicket')}
                        </button>
                    </div>
                </form>
            </div>
        </Layout>
    );
}

export default HelpdeskCreateTicket;